import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import useFetchWithMsal from '../../../hooks/useFetchWithMsal';
import { protectedResources } from '../../../authConfig';

const useInvoiceNotes = (invoice, onSaveNotes, refreshFiles = null) => {
  const [notes, setNotes] = useState('');
  const [previousNotes, setPreviousNotes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [noteToDeleteIndex, setNoteToDeleteIndex] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  const { enqueueSnackbar } = useSnackbar();
  
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const invoiceExists = !!invoice;
  
  if (!invoiceExists) {
    console.warn('⚠️ useInvoiceNotes: invoice prop is undefined or null');
  }

  useEffect(() => {
    if (invoice?.notes) {
      try {
        const parsedNotes = JSON.parse(invoice.notes);
        if (Array.isArray(parsedNotes)) {
          const notesWithAttachments = parsedNotes.map(note => {
            if (typeof note.text === 'string') {
              const attachmentMatch = note.text.match(/\[צרופות: ([^\]]+)\]$/);
              if (attachmentMatch) {
                const attachmentNames = attachmentMatch[1].split(', ');
                const textWithoutAttachments = note.text.replace(/\n\[צרופות: [^\]]+\]$/, '');
                return {
                  ...note,
                  text: textWithoutAttachments,
                  attachments: attachmentNames.map(name => ({ fileName: name.trim() })),
                  isAttachmentOnly: textWithoutAttachments.trim() === ''
                };
              }
              
              if (note.text.startsWith('[צרופות: ') && note.text.endsWith(']')) {
                const attachmentNames = note.text.replace(/^\[צרופות: /, '').replace(/\]$/, '').split(', ');
                return {
                  ...note,
                  text: '',
                  attachments: attachmentNames.map(name => ({ fileName: name.trim() })),
                  isAttachmentOnly: true
                };
              }
            }
            return {
              ...note,
              attachments: note.attachments || [],
              isAttachmentOnly: false
            };
          });
          setPreviousNotes(notesWithAttachments);
        } else if (typeof parsedNotes === 'string') {
          const attachmentMatch = parsedNotes.match(/\[צרופות: ([^\]]+)\]$/);
          let noteText = attachmentMatch 
            ? parsedNotes.replace(/\n\[צרופות: [^\]]+\]$/, '') 
            : parsedNotes;
          
          if (parsedNotes.startsWith('[צרופות: ') && parsedNotes.endsWith(']')) {
            noteText = '';
            const attachmentNames = parsedNotes.replace(/^\[צרופות: /, '').replace(/\]$/, '').split(', ');
            const attachments = attachmentNames.map(name => ({ fileName: name.trim() }));
            
            setPreviousNotes([{
              text: '',
              timestamp: new Date().toISOString(),
              user: 'Unknown',
              attachments,
              isAttachmentOnly: true
            }]);
            return;
          }
          
          const attachments = attachmentMatch 
            ? attachmentMatch[1].split(', ').map(name => ({ fileName: name.trim() }))
            : [];

          setPreviousNotes([{
            text: noteText,
            timestamp: new Date().toISOString(),
            user: 'Unknown',
            attachments,
            isAttachmentOnly: noteText.trim() === '' && attachments.length > 0
          }]);
        }
      } catch (e) {
        if (invoice.notes) {
          let noteText = invoice.notes;
          let attachments = [];
          let isAttachmentOnly = false;
          
          const attachmentMatch = invoice.notes.match(/\[צרופות: ([^\]]+)\]$/);
          if (attachmentMatch) {
            noteText = invoice.notes.replace(/\n\[צרופות: [^\]]+\]$/, '');
            attachments = attachmentMatch[1].split(', ').map(name => ({ fileName: name.trim() }));
            isAttachmentOnly = noteText.trim() === '';
          } else if (invoice.notes.startsWith('[צרופות: ') && invoice.notes.endsWith(']')) {
            noteText = '';
            const attachmentNames = invoice.notes.replace(/^\[צרופות: /, '').replace(/\]$/, '').split(', ');
            attachments = attachmentNames.map(name => ({ fileName: name.trim() }));
            isAttachmentOnly = true;
          }

          setPreviousNotes([{
            text: noteText,
            timestamp: new Date().toISOString(),
            user: 'Unknown',
            attachments,
            isAttachmentOnly
          }]);
        }
      }
    }
  }, [invoice?.notes]);

  const handleFileSelect = useCallback(async (event) => {
    if (!invoiceExists) {
      enqueueSnackbar('שגיאה: לא ניתן להעלות קבצים - פרטי החשבונית חסרים', { variant: 'error' });
      return;
    }

    const files = Array.from(event.target.files);
    setUploadError('');

    if (files.length === 0) return;

    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      uploaded: false,
      uploadedPath: null,
      error: null
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);
    
    enqueueSnackbar(`נבחרו ${newFiles.length} קבצים. לחץ "שמור" כדי להעלות אותם.`, { 
      variant: 'info' 
    });
  }, [invoiceExists, enqueueSnackbar]);

  const uploadFiles = useCallback(async (filesToUpload) => {
    if (!invoiceExists || !invoice?.booknum) {
      setUploadError('מספר חשבונית לא זמין');
      return [];
    }

    setIsUploadingFiles(true);
    const uploadedFiles = [];
    
    for (const fileItem of filesToUpload) {
      try {
        setSelectedFiles(prev => prev.map(f => 
          f.name === fileItem.name ? { ...f, uploading: true } : f
        ));

        const base64 = await fileToBase64(fileItem.file);
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileExtension = fileItem.name.split('.').pop();
        const uniqueFileName = `${invoice._id || invoice.id}_attachment_${timestamp}.${fileExtension}`;

        const uploadResponse = await execute(
          'POST',
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/upload-attachment`,
          {
            invoiceId: invoice._id || invoice.id,
            fileName: uniqueFileName,
            originalFileName: fileItem.name,
            fileContent: base64,
            contentType: fileItem.file.type
          }
        );

        const uploadedFile = {
          ...fileItem,
          uploading: false,
          uploaded: true,
          uploadedPath: uploadResponse.filePath,
          downloadUrl: uploadResponse.downloadUrl
        };
        
        uploadedFiles.push(uploadedFile);

        setSelectedFiles(prev => prev.map(f => 
          f.name === fileItem.name ? uploadedFile : f
        ));

      } catch (error) {
        console.error('Error uploading file:', error);
        
        setSelectedFiles(prev => prev.map(f => 
          f.name === fileItem.name ? { 
            ...f, 
            uploading: false, 
            error: error.response?.data?.message || 'שגיאה בהעלאת הקובץ'
          } : f
        ));

        enqueueSnackbar(`שגיאה בהעלאת הקובץ "${fileItem.name}": ${error.response?.data?.message || error.message || error}`, { 
          variant: 'error' 
        });
      }
    }

    setIsUploadingFiles(false);
    return uploadedFiles;
  }, [invoiceExists, invoice, enqueueSnackbar, execute]);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleRemoveFile = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    if (!invoiceExists || !invoice) {
      enqueueSnackbar('שגיאה: לא ניתן לשמור - פרטי החשבונית חסרים', { variant: 'error' });
      return;
    }
    
    const hasText = notes.trim().length > 0;
    const hasAttachments = selectedFiles.length > 0;
    
    if (!hasText && !hasAttachments) {
      enqueueSnackbar('יש להוסיף הערה או צrופה לפחות', { variant: 'warning' });
      return;
    }

    setIsSaving(true);

    try {
      let uploadedAttachments = [];
      
      if (hasAttachments) {
        uploadedAttachments = await uploadFiles(selectedFiles);
        
        const failedUploads = uploadedAttachments.filter(f => f.error);
        if (failedUploads.length > 0) {
          enqueueSnackbar(`נכשלה העלאת ${failedUploads.length} קבצים`, { variant: 'error' });
          return;
        }
      }

      if (hasText && !hasAttachments) {
        if (onSaveNotes) {
          await onSaveNotes(invoice, notes.trim(), false);
          
          const newNote = {
            text: notes.trim(),
            timestamp: new Date().toISOString(),
            user: 'Current User',
            attachments: [],
            isAttachmentOnly: false
          };
          setPreviousNotes(prev => [...prev, newNote]);
          setNotes('');
          
          enqueueSnackbar('ההערה נשמרה בהצלחה', { variant: 'success' });
        }
      }
      else if (!hasText && hasAttachments) {
        const attachmentsOnlyNote = `[צרופות: ${uploadedAttachments.map(f => f.name).join(', ')}]`;
        
        if (onSaveNotes) {
          await onSaveNotes(invoice, attachmentsOnlyNote, false);
          
          const newNote = {
            text: '',
            timestamp: new Date().toISOString(),
            user: 'Current User',
            attachments: uploadedAttachments.map(f => ({
              fileName: f.name,
              size: f.size
            })),
            isAttachmentOnly: true
          };
          setPreviousNotes(prev => [...prev, newNote]);
          setSelectedFiles([]);
          
          if (refreshFiles) {
            setTimeout(() => {
              refreshFiles();
            }, 500);
          }
          
          enqueueSnackbar(
            `${uploadedAttachments.length === 1 
              ? `הצrופה "${uploadedAttachments[0].name}" נשמרה בהצלחה` 
              : `${uploadedAttachments.length} צרופות נשמרו בהצלחה`}`, 
            { 
              variant: 'success',
              autoHideDuration: 4000
            }
          );
        }
      }
      else if (hasText && hasAttachments) {
        const attachmentsInfo = uploadedAttachments.map(f => ({
          fileName: f.name,
          size: f.size
        }));
        
        const noteTextWithAttachments = `${notes.trim()}\n[צרופות: ${attachmentsInfo.map(a => a.fileName).join(', ')}]`;
        
        if (onSaveNotes) {
          await onSaveNotes(invoice, noteTextWithAttachments, false);
          
          const newNote = {
            text: notes.trim(),
            timestamp: new Date().toISOString(),
            user: 'Current User',
            attachments: attachmentsInfo,
            isAttachmentOnly: false
          };
          
          setPreviousNotes(prev => [...prev, newNote]);
          setNotes('');
          setSelectedFiles([]);
          
          if (refreshFiles) {
            setTimeout(() => {
              refreshFiles();
            }, 500);
          }
          
          enqueueSnackbar('ההערה והצרופות נשמרו בהצלחה', { variant: 'success' });
        }
      }

    } catch (error) {
      console.error('Error saving note:', error);
      enqueueSnackbar(`שגיאה בשמירה: ${error.message || error}`, { 
        variant: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  }, [notes, selectedFiles, invoice, invoiceExists, onSaveNotes, enqueueSnackbar, uploadFiles, refreshFiles]);

  const openDeleteConfirmation = useCallback((index) => {
    setNoteToDeleteIndex(index);
    setShowDeleteConfirmation(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setShowDeleteConfirmation(false);
    setNoteToDeleteIndex(null);
  }, []);

  const confirmDeleteNote = useCallback(async () => {
    if (noteToDeleteIndex === null) return;

    if (!invoiceExists || !invoice) {
      enqueueSnackbar('שגיאה: לא ניתן למחוק - פרטי החשבונית חסרים', { variant: 'error' });
      return;
    }

    setIsDeleting(true);
    
    try {
      const noteToDelete = previousNotes[noteToDeleteIndex];
      let attachmentsDeletionResults = [];
      
      if (noteToDelete.attachments && noteToDelete.attachments.length > 0) {
        for (const attachment of noteToDelete.attachments) {
          try {
            console.log(`Attempting to delete attachment: ${attachment.fileName}`);
            const response = await execute(
              'DELETE',
              `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/attachment-by-name/${invoice._id || invoice.id}/${encodeURIComponent(attachment.fileName)}`
            );
            console.log(`Attachment deletion response:`, response);
            attachmentsDeletionResults.push({ fileName: attachment.fileName, success: true });
          } catch (attachmentError) {
            console.warn(`Failed to delete attachment ${attachment.fileName}:`, attachmentError);
            attachmentsDeletionResults.push({ 
              fileName: attachment.fileName, 
              success: false, 
              error: attachmentError.message 
            });
          }
        }
      }

      const updatedNotes = [...previousNotes];
      updatedNotes.splice(noteToDeleteIndex, 1);

      const notesToSave = updatedNotes.map(note => {
        let noteText = note.text || '';
        
        if (note.attachments && note.attachments.length > 0) {
          const attachmentNames = note.attachments.map(a => a.fileName).join(', ');
          if (noteText.trim() === '') {
            noteText = `[צרופות: ${attachmentNames}]`;
          } else {
            noteText += `\n[צרופות: ${attachmentNames}]`;
          }
        }
        
        return {
          text: noteText,
          timestamp: note.timestamp,
          user: note.user
        };
      });
      
      if (onSaveNotes) {
        try {
          await onSaveNotes(invoice, JSON.stringify(notesToSave), true, {
            deletedAttachments: attachmentsDeletionResults.filter(r => r.success).map(r => r.fileName),
            refreshFiles: true
          });
          
          setPreviousNotes(updatedNotes);
          
          if (attachmentsDeletionResults.some(r => r.success)) {
            console.log('Successfully deleted attachments, refreshing files list...');
            // Force a more robust refresh by calling refreshFiles twice with different delays
            // First immediate refresh
            if (refreshFiles) {
              refreshFiles();
            }
            
            // Second delayed refresh to ensure backend changes are picked up
            setTimeout(() => {
              if (refreshFiles) {
                console.log('Performing secondary refresh of files...');
                refreshFiles();
              }
            }, 1000);
          }
          
          const successfulDeletions = attachmentsDeletionResults.filter(r => r.success);
          const failedDeletions = attachmentsDeletionResults.filter(r => !r.success);
          
          if (failedDeletions.length === 0) {
            enqueueSnackbar('ההערה והצרופות נמחקו בהצלחה', { variant: 'success' });
          } else {
            enqueueSnackbar(`ההערה נמחקה. ${successfulDeletions.length} צרופות נמחקו, ${failedDeletions.length} צרופות נכשלו`, { variant: 'warning' });
          }
        } catch (callbackError) {
          console.error('Error in onSaveNotes callback during deletion:', callbackError);
          enqueueSnackbar(`שגיאה במחיקת ההערה: ${callbackError.message || callbackError}`, { 
            variant: 'error' 
          });
        }
      }
      
    } catch (error) {
      console.error('Error deleting note:', error);
      enqueueSnackbar(`שגיאה במחיקת ההערה: ${error.message || error}`, { 
        variant: 'error' 
      });
    } finally {
      setIsDeleting(false);
      closeDeleteConfirmation();
    }
  }, [noteToDeleteIndex, previousNotes, invoice, invoiceExists, onSaveNotes, enqueueSnackbar, closeDeleteConfirmation, execute, refreshFiles]);

  return {
    notes,
    setNotes,
    previousNotes,
    isSaving,
    isDeleting,
    handleSave,
    openDeleteConfirmation,
    showDeleteConfirmation,
    closeDeleteConfirmation,
    confirmDeleteNote,
    noteToDeleteIndex,
    selectedFiles,
    setSelectedFiles,
    isUploadingFiles,
    uploadError,
    handleFileSelect,
    handleRemoveFile,
    uploadFiles
  };
};

export default useInvoiceNotes;